/**
 * Tool + department visibility for the sidebar (and anywhere else that decides
 * what a user is allowed to SEE).
 *
 * Steven's rule: if a user has no access to a tool, hide the tool; if they have
 * no access to any tool in a section, hide the section. This drives visibility
 * off the SAME backend permissions the API authorizes with, so the sidebar can
 * never show a tool that then 403s.
 *
 * Binary access-grant model (spec 00_access-binary-grants.md, 2026-08-09): a
 * tool is permitted for a user when ANY of these hold:
 *   1. Super-admin. Checked first; skips every other check.
 *   2. The tool carries an `accessSlug` (shared/catalog.ts `Tool.accessSlug`)
 *      AND the user's permissions include that EXACT string. One grant row =
 *      one person + one accessSlug; there are no levels and no scope-prefix
 *      matching anymore (removed 2026-08-09 — see accessScopes.ts and
 *      Agents/specs/00_access-binary-grants.md for the prior TOOL_SCOPE
 *      design).
 *   3. The tool carries an explicit `capability` (the fixed Everyone set —
 *      Chat, HR Directory, Fleet Map) AND the user holds it (exact match).
 *      Every employee holds `core:read` automatically, so these are
 *      effectively ungated for staff.
 *   4. The tool declares NEITHER `accessSlug` NOR `capability`. Genuinely
 *      public and visible to everyone with a session.
 */

import { isLaunched, type Department, type Tool } from '@/data/tools.config';

/**
 * Is this tool permitted for a user with these permissions? See the module
 * doc-comment for the full rule. Does NOT consider `isLaunched`; callers that
 * care about the sidebar combine this with `isLaunched` via `visibleToolsFor`.
 */
export function isToolPermitted(
  tool: Tool,
  perms: string[] | undefined,
  superAdmin = false,
): boolean {
  if (superAdmin) return true;
  const held = perms ?? [];
  // 1. Binary grant: exact accessSlug match.
  if (tool.accessSlug) return held.includes(tool.accessSlug);
  // 2. Everyone tool: exact capability match (core:read, held by every employee).
  if (tool.capability) return held.includes(tool.capability);
  // 3. Neither: genuinely public.
  return true;
}

/**
 * True when `perms` (or super-admin status) grants a specific accessSlug.
 * The same primitive `isToolPermitted` uses internally, exposed directly for
 * page-level access-denied gates that need to check one slug without looking
 * up a catalog Tool object (e.g. a Stores HQ sub-page checking its own
 * `stores.scorecard` / `.questions` / `.broadcast` / `.photos` / `.variance`).
 */
export function hasAccessSlug(
  perms: string[] | undefined,
  slug: string,
  superAdmin = false,
): boolean {
  if (superAdmin) return true;
  return !!perms?.includes(slug);
}

/**
 * The tools of a department that should render in the sidebar: launched AND
 * permitted for the current user.
 */
export function visibleToolsFor(
  dept: Department,
  perms: string[] | undefined,
  superAdmin = false,
): Tool[] {
  return dept.tools.filter((t) => isLaunched(t) && isToolPermitted(t, perms, superAdmin));
}

// A department is visible when the user passes its own capability gate (when
// set) AND at least one of its tools is visible to them. As of 2026-08-09 no
// department declares `capability` anymore (each was superseded by per-tool
// accessSlug grants; see shared/catalog.ts history), so this is always true
// in practice, but the field/check stays in case a future department-wide
// gate is genuinely needed.
function isDeptCapabilityMet(
  dept: Department,
  perms: string[] | undefined,
  superAdmin = false,
): boolean {
  if (!dept.capability) return true;
  if (superAdmin) return true;
  return !!perms && perms.includes(dept.capability);
}

/**
 * Whether a department should render at all: its own capability gate passes and
 * it has at least one visible tool. Used for both the expanded section headers
 * and the collapsed icon rail so they can never disagree.
 */
export function isDeptVisible(
  dept: Department,
  perms: string[] | undefined,
  superAdmin = false,
): boolean {
  return (
    isDeptCapabilityMet(dept, perms, superAdmin) &&
    visibleToolsFor(dept, perms, superAdmin).length > 0
  );
}
